// guides.ts — draggable on-floor reference lines + intersection crosses.
//
// A "guide" is one line constrained to slide along a single axis:
//   - axis="x" → the line itself runs along world-X, and the user drags it
//     along world-Z (so on screen it slides "back/forward").
//   - axis="z" → runs along world-Z, drags along world-X.
//
// Crosses (`+`) are computed at every X×Z guide intersection.
//
// Scene-chrome category, exempt from web/CLAUDE.md hard-rule #1
// ("no procedural geometry for assets") — same exemption as the dot grid,
// link cables, and selection outlines: these are not assets.
//
// Public API:
//   - createGuidesGroup() → returns a THREE.Group sized for the scene
//   - rebuildGuides(group, settings)  → regenerates lines + crosses from
//     the persisted guide list. Call whenever guides[] or look-and-feel
//     settings change. Cheap (a handful of LineSegments).
//   - disposeGuides(group) → frees geometry/materials before unmount.
//   - hitTestGuide(group, ray, ...) → returns the guide id under a ray
//     (for click-select / drag-start). Tolerance is in world units.
//   - dragCoordForRay(axis, ray, plane) → projects a ray onto the floor
//     and returns the new coord for a guide on that axis. Used by the
//     drag handler.

import * as THREE from "three";
import type { Guide, SceneSettings } from "./types";

/** Minimal subset of settings the guide overlay reads. Any host (Scene or
 *  Iso builder) just needs to supply these — full SceneSettings satisfies
 *  the type via structural typing. */
export type GuideRenderSettings = {
  gridLines?: boolean;
  gridLinesShowCrosses?: boolean;
  gridLinesCrossSize?: number;
  gridLinesOpacityLight?: number;
  gridLinesOpacityDark?: number;
  /** Cross opacity is independent of line opacity. */
  gridLinesCrossOpacityLight?: number;
  gridLinesCrossOpacityDark?: number;
  gridLinesColorLight?: string;
  gridLinesColorDark?: string;
  /** Dash + gap length in metres. Small dashes + larger gaps = dotted look. */
  gridLinesDashSize?: number;
  gridLinesGapSize?: number;
  guides?: Guide[];
  /** "dark" or "light"; iso has no theme so it can pass "dark". */
  theme?: "dark" | "light";
  /** Used to size the line span; iso can pass `tileSize * levelCount` or similar. */
  groundSize?: number;
};

// Default dash/gap give a fine-stippled "dotted" feel. Sliders override
// per-profile. Min dash is clamped to 0.005 m so the user can't render
// invisible lines by accident.
const DEFAULT_DASH = 0.06;
const DEFAULT_GAP  = 0.22;

const GUIDE_Y = 0.001;          // float just above the floor to avoid z-fight
const SELECTED_TINT = 0x4ab1ff; // selection highlight — matches accent family
const HIT_TOLERANCE = 0.18;     // world units — fattens click-target

// Cross-segment template (two perpendicular short lines forming a `+`).
// Re-built per call so the crosses scale with `gridLinesCrossSize`.
function makeCrossGeometry(size: number): THREE.BufferGeometry {
  const half = size / 2;
  const positions = new Float32Array([
    -half, 0, 0,  half, 0, 0,    // horizontal arm (along X)
    0, 0, -half,  0, 0, half,    // depth arm (along Z)
  ]);
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  return g;
}

function lineMaterial(
  colorHex: number,
  opacity: number,
  dashed: boolean,
  dashSize: number = DEFAULT_DASH,
  gapSize: number = DEFAULT_GAP,
): THREE.LineBasicMaterial | THREE.LineDashedMaterial {
  const m = dashed
    ? new THREE.LineDashedMaterial({
        color: colorHex, transparent: true, opacity, depthWrite: false,
        dashSize: Math.max(0.005, dashSize),
        gapSize: Math.max(0.005, gapSize),
      })
    : new THREE.LineBasicMaterial({
        color: colorHex, transparent: true, opacity, depthWrite: false,
      });
  return m;
}

/** Build an empty group; call rebuildGuides() to populate. */
export function createGuidesGroup(): THREE.Group {
  const g = new THREE.Group();
  g.name = "guides";
  g.userData.isGuidesOverlay = true;
  // Render after solids so dashes show against them, but stays above the
  // dot-grid plane (which sits at y=0).
  g.renderOrder = 5;
  return g;
}

/** Resolved colour + per-element opacities for the current theme. */
function resolveLook(settings: GuideRenderSettings): {
  color: number;
  lineOpacity: number;
  crossOpacity: number;
} {
  const isDark = settings.theme === "dark";
  const colorStr = isDark
    ? (settings.gridLinesColorDark || "")
    : (settings.gridLinesColorLight || "");
  let color: number;
  if (colorStr) {
    color = new THREE.Color(colorStr).getHex();
  } else {
    // Auto from palette: light grey on dark, dark grey on light.
    color = isDark ? 0x9aa0aa : 0x4a4f57;
  }
  const lineOpacity = isDark
    ? (settings.gridLinesOpacityDark ?? 0.55)
    : (settings.gridLinesOpacityLight ?? 0.65);
  // Cross opacity defaults: a touch brighter than the lines so the markers
  // pop out at intersections. Falls back to the line opacity if unset
  // (existing profiles auto-keep their look).
  const crossOpacity = isDark
    ? (settings.gridLinesCrossOpacityDark ?? Math.min(1, lineOpacity + 0.15))
    : (settings.gridLinesCrossOpacityLight ?? Math.min(1, lineOpacity + 0.15));
  return { color, lineOpacity, crossOpacity };
}

/**
 * Rebuild the guide-line + cross overlay from the persisted state.
 * Idempotent: tears down any prior children and disposes their resources.
 *
 * @param selectedId  Optional guide id to render with the selected tint.
 */
export function rebuildGuides(
  group: THREE.Group,
  settings: GuideRenderSettings,
  /** Guide id rendered in the brand-blue selection tint with a small
   *  opacity bump so the user can see which one is the active target
   *  for drag/delete/duplicate. Pass null for none. */
  selectedId: string | null = null,
): void {
  // Tear down prior contents.
  for (let i = group.children.length - 1; i >= 0; i--) {
    const c = group.children[i];
    if ((c as any).geometry?.dispose) (c as any).geometry.dispose();
    const mat = (c as any).material;
    if (mat?.dispose) mat.dispose();
    group.remove(c);
  }

  group.visible = !!settings.gridLines;
  if (!settings.gridLines) return;

  const guides: Guide[] = settings.guides ?? [];
  if (guides.length === 0) return;

  const { color, lineOpacity, crossOpacity } = resolveLook(settings);
  const dashSize = settings.gridLinesDashSize ?? DEFAULT_DASH;
  const gapSize = settings.gridLinesGapSize ?? DEFAULT_GAP;
  // Clip guides to the plate the host owns. Scene passes its `groundSize`
  // (matches the floor plane); Iso passes its tile size per level. Either
  // way, lines stop at the plate edge instead of bleeding into empty
  // space — this is what makes the guides feel attached to the surface.
  const half = (settings.groundSize ?? 80) / 2;

  // ---- Lines (one Line per guide). Dot-stippled via LineDashedMaterial.
  for (const guide of guides) {
    const geom = new THREE.BufferGeometry();
    let positions: Float32Array;
    if (guide.axis === "x") {
      // Runs along world X; coord is its Z position.
      positions = new Float32Array([
        -half, GUIDE_Y, guide.coord,
         half, GUIDE_Y, guide.coord,
      ]);
    } else {
      // Runs along world Z; coord is its X position.
      positions = new Float32Array([
        guide.coord, GUIDE_Y, -half,
        guide.coord, GUIDE_Y,  half,
      ]);
    }
    geom.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    // The selected guide renders in the brand-blue accent tint with a
    // small opacity bump so the user can tell which one is the active
    // target for drag / delete / alt-drag duplicate. All others share
    // the same neutral colour and opacity.
    const isSelected = !!selectedId && guide.id === selectedId;
    const mat = lineMaterial(
      isSelected ? SELECTED_TINT : color,
      isSelected ? Math.min(1, lineOpacity + 0.25) : lineOpacity,
      true,
      dashSize,
      gapSize,
    );
    const line = new THREE.Line(geom, mat);
    if (mat instanceof THREE.LineDashedMaterial) line.computeLineDistances();
    line.userData.guideId = guide.id;
    line.userData.guideAxis = guide.axis;
    group.add(line);
  }

  // ---- Crosses at every X×Z intersection. Solid (not dashed) so they read
  // as discrete markers rather than continuations of the stippled lines.
  if (settings.gridLinesShowCrosses === false) return;
  const xLines = guides.filter((g) => g.axis === "x");
  const zLines = guides.filter((g) => g.axis === "z");
  if (xLines.length === 0 || zLines.length === 0) return;

  const crossSize = settings.gridLinesCrossSize ?? 0.25;
  const crossGeom = makeCrossGeometry(crossSize);
  const crossMat = lineMaterial(color, crossOpacity, false);
  for (const xL of xLines) {
    for (const zL of zLines) {
      // xL.coord = z position; zL.coord = x position.
      const cross = new THREE.LineSegments(crossGeom, crossMat);
      cross.position.set(zL.coord, GUIDE_Y, xL.coord);
      cross.userData.isGuideCross = true;
      group.add(cross);
    }
  }
}

/** Tear down the overlay's GPU resources. Call before discarding the group. */
export function disposeGuides(group: THREE.Group): void {
  for (let i = group.children.length - 1; i >= 0; i--) {
    const c = group.children[i];
    if ((c as any).geometry?.dispose) (c as any).geometry.dispose();
    const mat = (c as any).material;
    if (mat?.dispose) mat.dispose();
    group.remove(c);
  }
}

/**
 * Hit-test a ray against the guide group and return the closest guide id
 * within `HIT_TOLERANCE` world units, or null. Uses the ground-plane
 * projection of the ray.
 */
export function hitTestGuide(
  group: THREE.Group,
  raycaster: THREE.Raycaster,
  planeY?: number,
): { id: string; axis: "x" | "z" } | null {
  // The hit plane is at the group's world Y plus the geometry's local
  // GUIDE_Y offset. Iso passes its plate Y; Scene leaves planeY undefined
  // and falls back to the group's own Y (defaults to 0).
  const groupY = group.position.y;
  const y = (planeY ?? groupY) + GUIDE_Y;
  const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -y);
  const point = new THREE.Vector3();
  if (!raycaster.ray.intersectPlane(plane, point)) return null;
  // The line's coord is encoded in the geometry but interpreted in WORLD
  // space when comparing against the projected ray-plane point. Lines and
  // crosses live in local space; we need to compensate for the group's
  // X/Z offset (which is always 0 today, but cheap to be correct).
  const groupX = group.position.x;
  const groupZ = group.position.z;
  let best: { id: string; axis: "x" | "z"; dist: number } | null = null;
  for (const child of group.children) {
    const id = child.userData?.guideId as string | undefined;
    const axis = child.userData?.guideAxis as "x" | "z" | undefined;
    if (!id || !axis) continue;
    const geom = (child as THREE.Line).geometry as THREE.BufferGeometry;
    const pos = geom.getAttribute("position") as THREE.BufferAttribute;
    if (axis === "x") {
      const coordWorld = pos.getZ(0) + groupZ;
      const d = Math.abs(point.z - coordWorld);
      if (d < HIT_TOLERANCE && (!best || d < best.dist)) {
        best = { id, axis, dist: d };
      }
    } else {
      const coordWorld = pos.getX(0) + groupX;
      const d = Math.abs(point.x - coordWorld);
      if (d < HIT_TOLERANCE && (!best || d < best.dist)) {
        best = { id, axis, dist: d };
      }
    }
  }
  return best ? { id: best.id, axis: best.axis } : null;
}

/**
 * Project a ray onto the floor and return the snapped coord for dragging
 * a guide on `axis`. `snap` of 0 disables snapping (Shift-drag).
 */
export function dragCoordForRay(
  axis: "x" | "z",
  raycaster: THREE.Raycaster,
  snap: number,
  /** Y of the host plate (Iso) — Scene omits and the guide group defaults
   *  to floor level. */
  planeY: number = 0,
): number | null {
  const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -(planeY + GUIDE_Y));
  const point = new THREE.Vector3();
  if (!raycaster.ray.intersectPlane(plane, point)) return null;
  const raw = axis === "x" ? point.z : point.x;
  if (snap > 0) return Math.round(raw / snap) * snap;
  return raw;
}

/** Default settings to apply when migrating an old profile that has none. */
export function defaultGuideSettings(): Pick<
  SceneSettings,
  | "gridLines"
  | "gridLinesShowCrosses"
  | "gridLinesCrossSize"
  | "gridLinesOpacityLight"
  | "gridLinesOpacityDark"
  | "gridLinesCrossOpacityLight"
  | "gridLinesCrossOpacityDark"
  | "gridLinesColorLight"
  | "gridLinesColorDark"
  | "gridLinesDashSize"
  | "gridLinesGapSize"
  | "guides"
> {
  return {
    gridLines: false,
    gridLinesShowCrosses: true,
    gridLinesCrossSize: 0.25,
    gridLinesOpacityLight: 0.65,
    gridLinesOpacityDark: 0.55,
    gridLinesCrossOpacityLight: 0.85,
    gridLinesCrossOpacityDark: 0.75,
    gridLinesColorLight: "",
    gridLinesColorDark: "",
    gridLinesDashSize: DEFAULT_DASH,
    gridLinesGapSize: DEFAULT_GAP,
    guides: [],
  };
}
